/**
 * Utilitário para validação de horários de cronogramas
 * Centraliza a lógica de validação para evitar conflitos entre sistemas
 */
class HorarioValidator {
  /**
   * Valida os horários de um cronograma
   * @param {Object} cronograma - Objeto com os horários dos sistemas
   * @param {string} cronograma.horarioVcon - Horário do VCON (formato HH:MM)
   * @param {string} cronograma.horarioVpra - Horário do VPRA (formato HH:MM)
   * @param {string} cronograma.horarioVsuc - Horário do VSUC (formato HH:MM)
   * @param {string} cronograma.horarioVsup - Horário do VSUP (formato HH:MM)
   * @param {string} cronograma.horarioVsyn - Horário do VSYN (formato HH:MM)
   * @param {boolean} cronograma.vcon - Se VCON está habilitado
   * @param {boolean} cronograma.vpra - Se VPRA está habilitado
   * @param {boolean} cronograma.vsuc - Se VSUC está habilitado
   * @param {boolean} cronograma.vsup - Se VSUP está habilitado
   * @param {boolean} cronograma.vsyn - Se VSYN está habilitado
   * @returns {Object} - Resultado da validação
   */
  static validarHorarios(cronograma) {
    const conflitos = []
    const sistemasHabilitados = []

    // Coletar sistemas habilitados com seus horários
    if (cronograma.vcon && cronograma.horarioVcon) {
      sistemasHabilitados.push({ 
        nome: 'VCON', 
        horario: cronograma.horarioVcon,
        color: '#82B982'
      })
    }
    
    if (cronograma.vpra && cronograma.horarioVpra) {
      sistemasHabilitados.push({ 
        nome: 'VPRA', 
        horario: cronograma.horarioVpra,
        color: 'info'
      })
    }
    
    if (cronograma.vsuc && cronograma.horarioVsuc) {
      sistemasHabilitados.push({ 
        nome: 'VSUC', 
        horario: cronograma.horarioVsuc,
        color: 'success'
      })
    }
    
    if (cronograma.vsup && cronograma.horarioVsup) {
      sistemasHabilitados.push({ 
        nome: 'VSUP', 
        horario: cronograma.horarioVsup,
        color: 'warning'
      })
    }
    
    if (cronograma.vsyn && cronograma.horarioVsyn) {
      sistemasHabilitados.push({ 
        nome: 'VSYN', 
        horario: cronograma.horarioVsyn,
        color: '#B2C58A'
      })
    }

    // Se menos de 2 sistemas habilitados, não há como ter conflito
    if (sistemasHabilitados.length < 2) {
      return {
        valido: true,
        conflitos: [],
        sistemasHabilitados
      }
    }

    // Verificar todos os pares de sistemas
    for (let i = 0; i < sistemasHabilitados.length; i++) {
      for (let j = i + 1; j < sistemasHabilitados.length; j++) {
        const sistema1 = sistemasHabilitados[i]
        const sistema2 = sistemasHabilitados[j]
        
        const conflito = HorarioValidator.verificarConflito(sistema1, sistema2)
        if (conflito) {
          conflitos.push(conflito)
        }
      }
    }

    return {
      valido: conflitos.length === 0,
      conflitos,
      sistemasHabilitados
    }
  }

  /**
   * Verifica conflito entre dois sistemas
   * @param {Object} sistema1 - Primeiro sistema
   * @param {Object} sistema2 - Segundo sistema
   * @returns {string|null} - Mensagem de conflito ou null se não há conflito
   */
  static verificarConflito(sistema1, sistema2) {
    const horario1 = HorarioValidator.parseHorario(sistema1.horario)
    const horario2 = HorarioValidator.parseHorario(sistema2.horario)
    
    // Verificar se são exatamente iguais
    if (horario1.totalMinutos === horario2.totalMinutos) {
      return {
        tipo: 'mesmo_horario',
        mensagem: `${sistema1.nome} e ${sistema2.nome} estão configurados para o mesmo horário (${sistema1.horario})`,
        sistemas: [sistema1, sistema2]
      }
    }
    
    // Verificar se a diferença é menor que 10 minutos
    const diferencaMinutos = Math.abs(horario1.totalMinutos - horario2.totalMinutos)
    if (diferencaMinutos < 10) {
      return {
        tipo: 'intervalo_insuficiente',
        mensagem: `${sistema1.nome} (${sistema1.horario}) e ${sistema2.nome} (${sistema2.horario}) têm menos de 10 minutos de diferença (${diferencaMinutos} min)`,
        sistemas: [sistema1, sistema2],
        diferencaMinutos
      }
    }

    return null
  }

  /**
   * Converte horário string para objeto com horas, minutos e total de minutos
   * @param {string} horario - Horário no formato HH:MM
   * @returns {Object} - Objeto com horas, minutos e totalMinutos
   */
  static parseHorario(horario) {
    if (!horario || typeof horario !== 'string') {
      return { horas: 0, minutos: 0, totalMinutos: 0 }
    }

    const [horas, minutos] = horario.split(':')
    return {
      horas: parseInt(horas) || 0,
      minutos: parseInt(minutos) || 0,
      totalMinutos: (parseInt(horas) || 0) * 60 + (parseInt(minutos) || 0)
    }
  }

  /**
   * Calcula o horário mínimo permitido baseado no horário de fim de backup
   * @param {string|null} horarioFimBackup - Horário de fim de backup no formato HH:MM ou null
   * @returns {string|null} - Horário mínimo no formato HH:MM ou null se não houver horarioFimBackup
   * 
   * Lógica:
   * 1. Normaliza o horário para cima em múltiplos de 10 minutos
   * 2. Adiciona 30 minutos ao horário normalizado
   * 
   * Exemplo:
   * - horarioFimBackup = "02:32"
   * - Normaliza para cima: 02:32 -> 02:40 (arredonda minutos para múltiplo de 10)
   * - Adiciona 30 minutos: 02:40 -> 03:10
   * - Retorna: "03:10"
   */
  static calcularHorarioMinimo(horarioFimBackup) {
    if (!horarioFimBackup || horarioFimBackup.trim() === '') {
      return null;
    }

    // Validar formato HH:MM
    const horarioRegex = /^(\d{2}):(\d{2})$/;
    const match = horarioFimBackup.trim().match(horarioRegex);
    
    if (!match) {
      return null;
    }

    let horas = parseInt(match[1], 10);
    let minutos = parseInt(match[2], 10);

    // Validar valores
    if (horas < 0 || horas > 23 || minutos < 0 || minutos > 59) {
      return null;
    }

    // Normalizar minutos para cima em múltiplos de 10
    // Ex: 32 -> 40, 40 -> 40, 41 -> 50
    const minutosNormalizados = Math.ceil(minutos / 10) * 10;
    
    // Se minutos normalizados >= 60, adicionar 1 hora e ajustar minutos
    if (minutosNormalizados >= 60) {
      horas += 1;
      minutos = 0;
    } else {
      minutos = minutosNormalizados;
    }

    // Adicionar 30 minutos
    minutos += 30;
    
    // Ajustar overflow de minutos
    if (minutos >= 60) {
      horas += Math.floor(minutos / 60);
      minutos = minutos % 60;
    }

    // Ajustar overflow de horas (24h)
    if (horas >= 24) {
      horas = horas % 24;
    }

    // Formatar como HH:MM
    const horasStr = horas.toString().padStart(2, '0');
    const minutosStr = minutos.toString().padStart(2, '0');

    return `${horasStr}:${minutosStr}`;
  }

  /**
   * Compara dois horários no formato HH:MM
   * @param {string} horario1 - Primeiro horário (HH:MM)
   * @param {string} horario2 - Segundo horário (HH:MM)
   * @returns {number} - Negativo se horario1 < horario2, 0 se iguais, positivo se horario1 > horario2
   */
  static compararHorarios(horario1, horario2) {
    if (!horario1 || !horario2) {
      return 0;
    }

    const [h1, m1] = horario1.split(':').map(Number);
    const [h2, m2] = horario2.split(':').map(Number);

    const total1 = h1 * 60 + m1;
    const total2 = h2 * 60 + m2;

    return total1 - total2;
  }

  /**
   * Valida se um horário é maior ou igual ao horário mínimo
   * @param {string} horario - Horário a validar (HH:MM)
   * @param {string|null} horarioMinimo - Horário mínimo permitido (HH:MM) ou null
   * @returns {boolean} - true se horario >= horarioMinimo ou se horarioMinimo for null
   */
  static validarHorarioMinimo(horario, horarioMinimo) {
    if (!horarioMinimo) {
      return true; // Sem restrição se não houver horário mínimo
    }

    if (!horario) {
      return false; // Horário vazio não é válido se há mínimo
    }

    return HorarioValidator.compararHorarios(horario, horarioMinimo) >= 0;
  }

  /**
   * Formata horário para exibição
   * @param {string} timeString - Horário em qualquer formato
   * @returns {string} - Horário formatado como HH:MM
   */
  static formatTime(timeString) {
    if (!timeString) return ''
    
    // Se já está no formato HH:MM, retornar
    if (timeString.match(/^\d{2}:\d{2}$/)) {
      return timeString
    }
    
    try {
      const date = new Date(`2000-01-01T${timeString}`)
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    } catch {
      return timeString
    }
  }

  /**
   * Extrai horário de string para formato HH:MM
   * @param {string} timeString - Horário em qualquer formato
   * @returns {string} - Horário no formato HH:MM ou string vazia
   */
  static extractTime(timeString) {
    if (!timeString) return ''
    
    // Se já está no formato HH:MM, retornar
    if (timeString.match(/^\d{2}:\d{2}$/)) {
      return timeString
    }
    
    try {
      const date = new Date(`2000-01-01T${timeString}`)
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    } catch {
      return ''
    }
  }

  /**
   * Obtém as regras de validação para exibição
   * @returns {Array} - Array com as regras de validação
   */
  static getRegras() {
    return [
      'Sistemas não podem executar no mesmo horário',
      'Deve haver no mínimo 10 minutos entre a execução de sistemas diferentes'
    ]
  }

  /**
   * Obtém sugestões de horários válidos
   * @param {Array} sistemasHabilitados - Sistemas que estão habilitados
   * @returns {Array} - Array com sugestões de horários
   */
  static getSugestoesHorarios(sistemasHabilitados) {
    if (sistemasHabilitados.length === 0) return []

    const sugestoes = []
    const horariosBase = ['00:00', '02:00', '04:00', '06:00', '08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00', '22:00']
    
    // Para cada sistema, sugerir horários com intervalo de 2 horas
    sistemasHabilitados.forEach((sistema, index) => {
      if (index < horariosBase.length) {
        sugestoes.push({
          sistema: sistema.nome,
          horario: horariosBase[index],
          descricao: `Sugestão para ${sistema.nome}`
        })
      }
    })

    return sugestoes
  }
}

export default HorarioValidator 